// Environment Configuration
// In Vite, environment variables must be prefixed with VITE_ to be exposed to the client

import { Capacitor } from '@capacitor/core';

export const getApiBaseUrl = (): string => {
  const isNative = Capacitor.isNativePlatform();
  
  // For mobile apps, always use the full production/staging URL
  // Relative URLs don't work in Capacitor
  if (isNative) {
    // Check for mobile-specific API URL first
    if (import.meta.env.VITE_MOBILE_API_BASE_URL) {
      return import.meta.env.VITE_MOBILE_API_BASE_URL;
    }
    
    // Fallback to production API URL for mobile
    if (import.meta.env.VITE_API_BASE_URL) {
      return import.meta.env.VITE_API_BASE_URL;
    }
    
    // If no env var is set, use a default production URL (update this to your actual backend)
    console.warn('[Environment] No mobile API URL configured. Using default. Please set VITE_MOBILE_API_BASE_URL or VITE_API_BASE_URL');
    return 'https://dev.cuelive.ae/api/v1'; // Update this to your actual backend URL
  }
  
  // Web: explicit URL wins (e.g. static hosting to a public API).
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (import.meta.env.VITE_API_URL) {
    return `${import.meta.env.VITE_API_URL}/api/v1`;
  }

  // Dev: local backend (Vite has no /api proxy in this repo).
  if (import.meta.env.DEV) {
    return 'http://localhost:3010/api/v1';
  }

  // Production web (Docker/nginx): same-origin /api/v1 — nginx proxies to the real API (Organizer pattern).
  return '/api/v1';
};

export const getWebSocketUrl = (): string => {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  const apiBaseUrl = getApiBaseUrl();

  if (apiBaseUrl.startsWith('/')) {
    if (typeof window !== 'undefined') {
      const isHttps = window.location.protocol === 'https:';
      return `${isHttps ? 'wss:' : 'ws:'}//${window.location.host}`;
    }
    return '';
  }

  let baseUrl = apiBaseUrl.replace(/\/api\/v1\/?$/, '');

  if (baseUrl.startsWith('https://')) {
    return baseUrl.replace('https://', 'wss://');
  }
  if (baseUrl.startsWith('http://')) {
    return baseUrl.replace('http://', 'ws://');
  }

  return 'ws://localhost:3010';
};

export const getGoogleClientId = (): string => {
  // Get Google OAuth Client ID from environment variable
  // Must be prefixed with VITE_ to be exposed to the client
  if (import.meta.env.VITE_GOOGLE_CLIENT_ID) {
    return import.meta.env.VITE_GOOGLE_CLIENT_ID;
  }
  
  // Fallback - user should set this in their .env file
  console.warn('VITE_GOOGLE_CLIENT_ID is not set. Please add it to your .env file.');
  return '1048482652880-nm5ndnsvtb2ulbmdq6ub6gek2a7j3tt9.apps.googleusercontent.com';
};

/** VAPID key for FCM Web Push (Firebase Console → Cloud Messaging → Web → Key pair). */
export const getVapidKey = (): string => {
  const key = import.meta.env.VITE_VAPID_KEY;
  if (key) return key;
  return '';
};

export const environment = {
  apiBaseUrl: getApiBaseUrl(),
  webSocketUrl: getWebSocketUrl(),
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  mode: import.meta.env.MODE,
} as const;

export default environment;

